import { useQuery } from "@tanstack/react-query";
import { Box, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../contexts/LocalizationContext";
import { fetchAllInstagramPosts } from "../api/auth";
import type { InstagramPostsPage } from "../api/types";

function instagramImgSrc(post: { thumbnailUrl: string | null; mediaUrl: string | null; mediaType: string }) {
  if (post.thumbnailUrl) return post.thumbnailUrl;
  if (post.mediaType !== "VIDEO" && post.mediaUrl) return post.mediaUrl;
  return null;
}

export default function NewsPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const { data, isLoading } = useQuery<InstagramPostsPage>({
    queryKey: ["instagramAllPosts"],
    queryFn: () => fetchAllInstagramPosts(1, 48),
    staleTime: 5 * 60_000,
  });

  return (
    <Box sx={{ flex: 1, backgroundColor: "#F6F6F6", display: "flex", flexDirection: "column", pb: '30px' }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 2, py: 1.5, position: "sticky", top: 0,
        backgroundColor: "#fff", zIndex: 5, borderBottom: "1px solid #F0F0F0",
      }}>
        <Box
          onClick={() => navigate(-1)}
          sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 36, height: 36, ml: "-8px", cursor: "pointer",
            borderRadius: "10px", fontSize: "28px", fontWeight: 300, lineHeight: 1, color: "#1A1A1A",
            "&:active": { backgroundColor: "#F5F5F5" },
          }}
        >‹</Box>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1A1A1A" }}>
          {t("home.news")}
        </Typography>
      </Box>

      {/* Grid */}
      <Box sx={{ px: 2, pt: "14px" }}>
        {isLoading ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {[0, 1, 2, 3, 4, 5].map(i => (
              <Box key={i}>
                <Skeleton variant="rounded" sx={{ width: "100%", aspectRatio: "1", borderRadius: "12px", mb: "6px" }} />
                <Skeleton variant="text" height={14} />
                <Skeleton variant="text" width="70%" height={14} />
              </Box>
            ))}
          </Box>
        ) : !data || data.posts.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 6, color: "#8A8A8A", fontSize: "14px" }}>
            {t("common.noItems")}
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {data.posts.map(post => {
              const imgSrc = instagramImgSrc(post);
              return (
                <Box
                  key={post.id}
                  component="a"
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: "none", cursor: "pointer", display: "block",
                    background: "#fff", borderRadius: "12px", overflow: "hidden",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                    transition: "transform 0.12s ease",
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  <Box sx={{ position: "relative", width: "100%", aspectRatio: "1", background: "#E8E8E8" }}>
                    {imgSrc
                      ? <img src={imgSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      : (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <rect x="2" y="2" width="20" height="20" rx="5" stroke="#C0C0C0" strokeWidth="1.5"/>
                            <circle cx="12" cy="12" r="4" stroke="#C0C0C0" strokeWidth="1.5"/>
                            <circle cx="17.5" cy="6.5" r="1" fill="#C0C0C0"/>
                          </svg>
                        </Box>
                      )
                    }
                  </Box>
                  {post.caption && (
                    <Box sx={{ p: "8px 10px 10px" }}>
                      <Typography sx={{
                        fontSize: "12px", color: "#444", lineHeight: 1.35,
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {post.caption}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
